import React, { useState, useEffect, useContext, useCallback } from 'react'; // 1. Added useEffect, useContext
import { AuthContext } from '../context/AuthContext'; // 2. Import AuthContext
import { useNotification } from '../context/NotificationContext';
import api from '../utils/api'; // 3. Import your api instance

import Feed from '../components/Feed/Feed';
import Modals from '../components/Modals';
import DiscoverView from '../components/DiscoverView';
import ProfileView from '../components/ProfileView';
import NotificationsView from '../components/NotificationsView'; 
import SettingsView from '../components/SettingsView';
import ConnectionsView from '../components/ConnectionsView';
import FavouritesView from '../components/FavouritesView';
import Icon from '../components/Icon';
import '../css/app.css';


export default function Dashboard() {
  const { user, logout } = useContext(AuthContext); // 1. Destructured user
  const { hasUnread, unreadCount } = useNotification();

  // console.log("Red Dot Status:", hasUnread);
  
  // Navigation & View States
  const [currentView, setCurrentView] = useState('home');
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [viewedUserId, setViewedUserId] = useState(null); // 🆕 Add this
  const [targetSettingsTab, setTargetSettingsTab] = useState('privacy'); // 🆕 ADD THIS

  const [notificationPostId, setNotificationPostId] = useState(null); 
  const [notificationCommentId, setNotificationCommentId] = useState(null);

  const [viewedProfile, setViewedProfile] = useState(null);
  
  const [platformsExpanded, setPlatformsExpanded] = useState(false);
  const [activeModal, setActiveModal] = useState(null);

  // --- Dashboard States ---
  const [profile, setProfile] = useState(null);
  const [userLinks, setUserLinks] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null); // 🚨 New Error State
  const [followStatus, setFollowStatus] = useState('connect'); // 'connect', 'pending', or 'connected'



  // =========================================================
  // 1. DEFINE TOAST FIRST (So everything below can safely use it)
  // =========================================================
  const showToast = useCallback((message) => {
    alert(`Toast Notice: ${message}`);
  }, []); // The empty array ensures this is created ONLY ONCE

  // =========================================================
  // 2. DEFINE THE FETCH HANDLER (Safely referencing showToast)
  // =========================================================
  const fetchProfileAndLinks = useCallback(async () => {
    try {
      setLoading(true);
      setError(null); // Reset any existing errors on retry

      // Standardizing the frontend to use Promise.all too!
      const [profileRes, linksRes] = await Promise.all([
        api.get('/profiles/me'),
        api.get('/portfolio/links')
      ]);

      // 1. Process Profile
      setProfile(profileRes.data.profile);

      // 2. Process Platform Links
      const data = linksRes.data;
      const rawLinks = Array.isArray(data) ? data : (data?.links || []);
      const linksMap = {};
      
      if (Array.isArray(rawLinks)) {
        rawLinks.forEach(item => {
          const p = (item.platform || item.platform_name || '').toLowerCase();
          const url = item.profile_url || item.url || '';
          if (p) linksMap[p] = url;
        });
      }
      setUserLinks(linksMap);

    } catch (err) {
      console.error("Dashboard initialization error:", err);
      
      // 🚨 INTERCEPT 401 UNAUTHORIZED
      if (err.response?.status === 401) {
        showToast("Your session has expired. Logging you out...");
        logout(); // This clears tokens and redirects to /login
        return;   // Exit early so we don't set the dashboard error state
      }

      // Capture a human-readable message to display to the user
      setError(
        err.response?.data?.message || 
        "Unable to connect to the server. Please check your internet connection and try again."
      );
    } finally {
      setLoading(false);
    }
  }, [logout, showToast]);

  // useEffect to fetch the target profile dynamically
  useEffect(() => {
    if (currentView === 'profile' && viewedUserId) {
      const fetchTargetProfile = async () => {
        try {
          setViewedProfile(null);
          setFollowStatus('connect'); 
          
          const [profileRes, statusRes] = await Promise.all([
            api.get(`/profiles/${viewedUserId}`),
            api.get(`/connections/status/${viewedUserId}`).catch(() => ({ data: { status: 'none' } })) 
          ]);
          
          setViewedProfile(profileRes.data.profile || profileRes.data);
          
          const backendStatus = statusRes.data?.status || 'none';
          if (backendStatus === 'pending') setFollowStatus('pending');
          else if (backendStatus === 'accepted') setFollowStatus('connected');
          else setFollowStatus('connect');
  
        } catch (err) {
          console.error("Failed to load creative's profile:", err);
          // 🚨 FIX: Prevent infinite loading state
          showToast("Failed to load this profile. It may no longer exist.");
          setViewedUserId(null);  // Revert back to logged-in user profile
          setCurrentView('home'); // Safely redirect away from the broken state
        }
      };
      fetchTargetProfile();
    }
  }, [currentView, viewedUserId, showToast]); // Added showToast dependency


  // =========================================================
  // 🆕 ADD THIS NEW USEEFFECT HERE
  // =========================================================
  useEffect(() => {
    // If the user navigates away from Profile and Discover, wipe the search state
    if (currentView !== 'profile' && currentView !== 'discover') {
      setViewedUserId(null);
      setViewedProfile(null); 
      setNotificationPostId(null);
      setNotificationCommentId(null); // 🆕 ADD THIS: Wipe the comment target
    }
  }, [currentView]);


  // =========================================================
  // 3. RUN INITIALIZATION
  // =========================================================
  useEffect(() => {
    fetchProfileAndLinks();
  }, [fetchProfileAndLinks]);


  // Helper to render platform links dynamically while retaining static styling, plan limitations, and redirects
  const renderPlatformItem = (platformKey, icon, label, isInitiallyLocked = false) => {
    const url = userLinks[platformKey];
    const hasLink = !!url;
    
    // Check if the plan is PRO to completely unlock the items, or keep the initial limit
    const userPlan = user?.plan || profile?.plan || 'starter';
    const isLocked = isInitiallyLocked && (userPlan === 'starter');

    const formattedUrl = hasLink ? (url.startsWith('http') ? url : `https://${url}`) : '#';

    if (isLocked) {
      return (
        <div 
          className="nav-item locked" 
          key={platformKey}
          onClick={() => {
            setActiveModal('upgrade-modal');
            showToast("Upgrade your plan to connect more platform links!");
            setIsMobileMenuOpen(false); // 👈 1. Closes menu when upgrade modal opens
          }}
          style={{ cursor: 'pointer' }}
        >
          <span className="nav-icon">{icon}</span> {label} <span className="nav-lock">🔒</span>
        </div>
      );
    }

    if (hasLink) {
      return (
        <a 
          key={platformKey}
          href={formattedUrl} 
          target="_blank" 
          rel="noopener noreferrer" 
          className="nav-item linked-platform"
          style={{ textDecoration: 'none', color: 'inherit', display: 'flex', alignItems: 'center', width: '100%' }}
          onClick={() => setIsMobileMenuOpen(false)} // 👈 2. Closes menu when opening link in new tab
        >
          <span className="nav-icon">{icon}</span> {label} 
          <span style={{ marginLeft: 'auto', fontSize: '10px', color: 'var(--teal)' }}>✓</span>
        </a>
      );
    }

    return (
      <div 
        key={platformKey}
        className="nav-item unlinked-platform" 
        style={{ opacity: 0.6, cursor: 'pointer' }}
        onClick={() => {
          setCurrentView('settings');
          showToast(`Connect your ${label} in Settings!`);
          setIsMobileMenuOpen(false); // 👈 3. Closes menu when jumping to settings
        }}
      >
        <span className="nav-icon">{icon}</span> {label}
      </div>
    );
  };

// =========================================================
  // INITIAL LOADING STATE
  // Blocks the main UI from rendering until profile & links arrive
  // =========================================================
  if (loading) {
    return (
      <div 
        className="buniverse-dashboard-wrapper" 
        style={{ 
          display: 'flex', 
          flexDirection: 'column',
          justifyContent: 'center', 
          alignItems: 'center', 
          height: '100vh',
          backgroundColor: 'var(--bg-base)' // Assuming you have a CSS var like this
        }}
      >
        {/* You can replace this with a spinner or a custom SVG */}
        <div className="logo" style={{ fontSize: '2rem', marginBottom: '1rem' }}>
          buni<span>verse</span>
        </div>
        <div style={{ color: 'var(--text2)', fontSize: '14px' }}>
          Loading your universe...
        </div>
      </div>
    );
  }

  // =========================================================
  // ERROR STATE
  // Displays if the initial data fetch fails (e.g., network drop)
  // =========================================================
  if (error) {
    return (
      <div 
        className="buniverse-dashboard-wrapper" 
        style={{ 
          display: 'flex', 
          flexDirection: 'column',
          justifyContent: 'center', 
          alignItems: 'center', 
          height: '100vh',
          backgroundColor: 'var(--bg-base)', // Using your app's CSS variables
          padding: '20px',
          textAlign: 'center'
        }}
      >
        {/* Dimmed logo to indicate a disconnected state */}
        <div className="logo" style={{ fontSize: '2rem', marginBottom: '1rem', opacity: 0.5 }}>
          buni<span>verse</span>
        </div>
        
        <div style={{ color: 'var(--text1)', fontSize: '1.2rem', marginBottom: '10px' }}>
          Connection Lost
        </div>
        
        {/* Displays the dynamic error message captured from the catch block */}
        <div style={{ color: 'var(--text3)', fontSize: '14px', marginBottom: '24px', maxWidth: '400px', lineHeight: '1.5' }}>
          {error}
        </div>
        
        {/* The Retry Button */}
        <button 
          onClick={fetchProfileAndLinks}
          style={{
            padding: '10px 24px',
            backgroundColor: 'var(--teal)', // Assuming teal is your primary action color
            color: '#fff',
            border: 'none',
            borderRadius: '8px',
            cursor: 'pointer',
            fontWeight: '600',
            fontSize: '14px',
            transition: 'opacity 0.2s ease'
          }}
          onMouseOver={(e) => e.target.style.opacity = 0.8}
          onMouseOut={(e) => e.target.style.opacity = 1}
        >
          Try Again
        </button>
      </div>
    );
  }


  // =========================================================
  // MAIN DASHBOARD RENDER
  // =========================================================
  return (
    <div className="buniverse-dashboard-wrapper">
      {/* TOPBAR */}
      <div className="topbar">
        <div className="logo">buni<span>verse</span></div>
        <div className="topbar-center">
          <button className={`top-tab ${currentView === 'home' ? 'active' : ''}`} onClick={() => setCurrentView('home')}>Home</button>
          <button className={`top-tab ${currentView === 'discover' ? 'active' : ''}`} onClick={() => setCurrentView('discover')}>Discover</button>
          <button className={`top-tab ${(currentView === 'profile' && !viewedUserId) ? 'active' : ''}`} onClick={() => { setViewedUserId(null); setCurrentView('profile'); }}>Profile</button>
          <button className={`top-tab ${currentView === 'notifications' ? 'active' : ''}`} onClick={() => setCurrentView('notifications')}>Notifications</button>
          <button className={`top-tab ${currentView === 'settings' ? 'active' : ''}`} onClick={() => setCurrentView('settings')}>Settings</button>
        </div>
        <div className="topbar-right">
          <div className="verify-chip" onClick={() => setActiveModal('verify-modal')}>✦ Get Verified</div>
          <div className="plan-badge plan-free" id="plan-pill" onClick={() => setActiveModal('upgrade-modal')}>Starter</div>
          <div className={`notif-btn ${currentView === 'notifications' ? 'active' : ''}`}
            onClick={() => setCurrentView(currentView === 'notifications' ? 'home' : 'notifications')} 
            style={{ position: 'relative', cursor: 'pointer' }}
          >
          🔔
          {hasUnread && (
            <span style={{
              position: 'absolute',
              top: '-2px',
              right: '-2px',
              width: '10px',
              height: '10px',
              backgroundColor: 'var(--red, #ff4444)',
              borderRadius: '50%',
              border: '2px solid var(--bg-base, white)'
            }}></span>
          )}
        </div>        

          {/* Profile initials if available */}
           <div className="user-av" onClick={() => setActiveModal('user-modal')}>
             {profile ? profile.display_name.charAt(0) : '...'}
           </div>
        </div>
      </div>

      {/* LAYOUT CONTAINER */}
      <div className="layout">


        {/* SIDEBAR */}
        <div className={`sidebar ${isMobileMenuOpen ? 'mobile-expanded' : ''}`}>
  <div style={{ height: '8px' }}></div>
  
  <div className="sb-label-row desktop-only"><span className="sb-label">Menu</span></div>
  
  {/* --- 🌟 THE ESSENTIAL 4 --- */}
  <div className={`nav-item ${currentView === 'home' ? 'active' : ''}`} onClick={() => {setCurrentView('home'); setIsMobileMenuOpen(false);}}>
    <span className="nav-icon"><Icon name="home" active={currentView === 'home'} /></span> Home
  </div>
  
  <div className={`nav-item ${currentView === 'discover' ? 'active' : ''}`} onClick={() => {setCurrentView('discover'); setIsMobileMenuOpen(false);}}>
    <span className="nav-icon"><Icon name="discover" active={currentView === 'discover'} /></span> Discover
  </div>
  
  <div className={`nav-item ${(currentView === 'profile' && !viewedUserId) ? 'active' : ''}`} onClick={() => { setViewedUserId(null); setCurrentView('profile'); setIsMobileMenuOpen(false);}}>
    <span className="nav-icon"><Icon name="profile" active={currentView === 'profile' && !viewedUserId} /></span> Profile
  </div>

  <div className={`nav-item ${currentView === 'notifications' ? 'active' : ''}`} onClick={() => {setCurrentView('notifications'); setIsMobileMenuOpen(false);}}>
    <span className="nav-icon"><Icon name="alerts" active={currentView === 'notifications'} /></span> Notifications
    {unreadCount > 0 && (
      <span className="nav-alert" id="sb-notif-count">
        {unreadCount > 99 ? '99+' : unreadCount}
      </span>
    )}
  </div>

  {/* --- 🍔 THE "MORE" BUTTON --- */}
  <div className="nav-item mobile-only" onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}>
    <span className="nav-icon"><Icon name={isMobileMenuOpen ? 'close' : 'more'} active={isMobileMenuOpen} /></span> 
    {isMobileMenuOpen ? 'Close' : 'More'}
  </div>

  {/* --- 📂 SECONDARY ITEMS --- */}
  <div className="secondary-items-container">
    <div className={`nav-item ${currentView === 'messages' ? 'active' : ''}`} onClick={() => {setCurrentView('messages'); setIsMobileMenuOpen(false);}}>
      <span className="nav-icon"><Icon name="messages" active={currentView === 'messages'} /></span> Messages
    </div>

    <div className={`nav-item ${currentView === 'bookings' ? 'active' : ''}`} onClick={() => {setCurrentView('bookings'); setIsMobileMenuOpen(false);}}>
      <span className="nav-icon"><Icon name="bookings" active={currentView === 'bookings'} /></span> Bookings
    </div>
    
    <div className="sb-divider desktop-only"></div>
    
    <div className="sb-label-row desktop-only"><span className="sb-label">My Lists</span></div>
    
    <div className={`nav-item ${currentView === 'favourites' ? 'active' : ''}`} onClick={() => {setCurrentView('favourites'); setIsMobileMenuOpen(false);}}>
      <span className="nav-icon"><Icon name="favourites" active={currentView === 'favourites'} /></span> Favourites <span className="nav-badge" id="fav-count"></span>
    </div>

    <div className={`nav-item ${currentView === 'connections' ? 'active' : ''}`} onClick={() => {setCurrentView('connections'); setIsMobileMenuOpen(false);}}>
      <span className="nav-icon"><Icon name="connections" active={currentView === 'connections'} /></span> Connections
    </div>
    
    <div className="sb-divider desktop-only"></div>

    {/* PLATFORMS SECTION */}
    <div className="sb-label-row" style={{ cursor: 'pointer' }} onClick={() => setPlatformsExpanded(!platformsExpanded)}>
      <span className="sb-label">Platforms</span>
      <button className="sb-collapse" id="sb-collapse-btn" style={{ transform: platformsExpanded ? 'rotate(180deg)' : 'none' }}>▾</button>
    </div>
    
    <div className="collapsible-section" id="platform-list" style={{ maxHeight: '300px', overflowY: 'auto' }}>
      {renderPlatformItem('instagram', '📷', 'Instagram')}
      {renderPlatformItem('youtube', '▶️', 'YouTube')}
      {renderPlatformItem('behance', '🎨', 'Behance')}
      {renderPlatformItem('dribbble', '🏀', 'Dribbble')}
      
      {platformsExpanded ? (
        <div id="extra-platforms">
          {renderPlatformItem('spotify', '🎵', 'Spotify')}
          {renderPlatformItem('twitter', '🐦', 'X / Twitter')}
        </div>
      ) : (
        <div id="locked-extra">
          {renderPlatformItem('spotify', '🎵', 'Spotify', true)}
          {renderPlatformItem('twitter', '🐦', 'X / Twitter', true)}
        </div>
      )}
      
      <div id="platform-nudge">
        <div className="upgrade-nudge">
          <div className="un-title">4-platform limit</div>
          <div className="un-sub">Upgrade for unlimited platform links.</div>
          <button className="un-btn" onClick={() => { setActiveModal('upgrade-modal'); setIsMobileMenuOpen(false); }}>
            Upgrade ↗
          </button>
        </div>
      </div>
    </div>

    {/* ACCOUNT SECTION */}
    <div className="sb-label-row"><span className="sb-label">Account</span></div>
    <div className={`nav-item ${currentView === 'settings' ? 'active' : ''}`} onClick={() => { setCurrentView('settings'); setIsMobileMenuOpen(false); }}>
      <span className="nav-icon"><Icon name="settings" active={currentView === 'settings'} /></span> Settings
    </div>
    
    <div className="nav-item" onClick={() => { setActiveModal('upgrade-modal'); setIsMobileMenuOpen(false); }}>
      <span className="nav-icon"><Icon name="upgrade" active={false} /></span> Upgrade Plan
    </div>

    <div className="nav-item" onClick={() => { setActiveModal('verify-modal'); setIsMobileMenuOpen(false); }}>
      <span className="nav-icon"><Icon name="verify" active={false} /></span> Get Verified
    </div>
  
    <div className="sb-divider"></div>

    {/* AD SLOT */}
    <div id="ad-slot">
      <div className="ad-slot">
        <div className="ad-label">Sponsored</div>
        <div className="ad-brand">Adobe Creative Cloud</div>
        <div className="ad-content">Bring your boldest creative ideas to life.</div>
        <span className="ad-cta">Learn more →</span>
      </div>
    </div>

  </div>
</div>
        {/* Closes .sidebar */} 


        {/* MAIN VIEWS FIELD */}
        <div className="main">

          {/* HOME VIEW */}
          <div className={`view ${currentView === 'home' ? 'active' : ''}`} id="view-home">
            <div className="stories-strip">
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px', cursor: 'pointer' }} onClick={() => setActiveModal('story-upload-modal')}>
                <div className="add-story">＋</div>
                <span style={{ fontSize: '9px', color: 'var(--text3)' }}>Add story</span>
              </div>
              <div className="story-item" onClick={() => showToast("Viewing Kofi's story — 6h left")}>
                <div className="story-av-wrap"><div className="story-av-inner">🎵</div></div>
                <span className="story-name">Kofi</span><span className="story-timer">6h</span>
              </div>
            </div>

            {/* DYNAMIC REACT ENGINE */}
            <Feed 
              showToast={showToast} 
              onSharePost={(post) => setActiveModal('share-modal')}
              socket={null} 
            />
          </div> {/* Closes #view-home */}

          {/* DISCOVER VIEW */}
          {currentView === 'discover' && (
            <DiscoverView 
              setCurrentView={setCurrentView} 
              setActiveModal={setActiveModal} 
              setViewedUserId={setViewedUserId} // 🆕 Pass this down
            />
          )}
     
          {/* PROFILE VIEW */}
          {currentView === 'profile' && (
          <ProfileView 
            profile={viewedUserId ? viewedProfile : profile} // 🛠 Toggles between target and you
            targetPostId={notificationPostId} 
            targetCommentId={notificationCommentId} // Tell the profile which comment to highlight
            currentUser={user} // 🎯 THIS IS THE MISSING PIECE!
            setActiveModal={setActiveModal} 
            showToast={showToast} 
            setCurrentView={setCurrentView} 
            setTargetSettingsTab={setTargetSettingsTab}  
            followStatus={followStatus} // Tells the profile which button state to show

          />
           )}

          {/* FAVOURITES VIEW */}
          {currentView === 'favourites' && (
            <FavouritesView 
              showToast={showToast}
              setViewedUserId={setViewedUserId}
              setCurrentView={setCurrentView}
            />
          )}

          {/* CONNECTIONS VIEW */}
          {currentView === 'connections' && (
            <ConnectionsView 
              showToast={showToast}
              setViewedUserId={setViewedUserId}
              setCurrentView={setCurrentView}
          />
          )}       

          {/* NOTIFICATIONS VIEW */}
          {currentView === 'notifications' && (
            <NotificationsView 
              profile={profile} //
              showToast={showToast} 
              setCurrentView={setCurrentView}
              setViewedUserId={setViewedUserId}
              setNotificationPostId={setNotificationPostId} 
              setNotificationCommentId={setNotificationCommentId} // Let notifications set the comment ID on click
            />
          )}

          {/* SETTINGS VIEW */}
          {currentView === 'settings' && (
            <SettingsView
              profile={profile} 
              showToast={showToast} 
              setActiveModal={setActiveModal}
              defaultTab={targetSettingsTab} // 🆕 ADD THIS 
            />
          )}

          {/* ========================================================= */}
          {/* RENDER ALL OUR MODALS HERE OVER THE ENTIRE DASHBOARD      */}
          {/* ========================================================= */}
          
          <Modals 
            activeModal={activeModal} 
            setActiveModal={setActiveModal} 
            showToast={showToast}
            logout={logout}
            setCurrentView={setCurrentView}
            profile={profile} // Dynamically passes the right user info

            currentUser={user} // 🎯 THIS IS THE MISSING PIECE!
          />

        </div> {/* Closes .main */}
      </div> {/* Closes .layout */}
    </div> /* Closes .buniverse-dashboard-wrapper */
  );
}